import { motion, AnimatePresence } from "framer-motion";
import { Clock, X, Play, Trash2 } from "lucide-react";
import type { Playbook } from "@workspace/api-client-react";
import { cn } from "@/lib/utils";
import { formatPlaybookDurationLabel, truncatePlaybookDescription } from "@/lib/playbook-utils";
import { PlaybookThumbnail, PlaybookSourceIcon, playbookSourceLabel, playbookRecallStep } from "@/components/playbook-thumbnail";
import { PlaybookTitle } from "@/components/playbook-title";

export const CARD_BACKGROUNDS = [
  "card-lavender",
  "card-sage",
  "card-golden",
  "card-blush",
  "card-sky",
  "card-terra",
] as const;

export type CardBg = (typeof CARD_BACKGROUNDS)[number];

/**
 * The "what is this trigger, and what happens if I start it" bottom sheet —
 * shared by the Library page and the Home page's swim lanes, so clicking a
 * playbook card always previews before committing to a session, never starts
 * one immediately.
 */
export function PlaybookPreviewSheet({
  playbook,
  cardBg,
  onClose,
  onStart,
  isStarting,
  startError,
  onDelete,
}: {
  playbook: Playbook | null;
  cardBg: CardBg | null;
  onClose: () => void;
  onStart: () => void;
  isStarting: boolean;
  startError?: string;
  /** Omit to hide the delete control regardless of whether the playbook is deletable. */
  onDelete?: () => void;
}) {
  return (
    <AnimatePresence>
      {playbook && cardBg && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm"
          />

          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className={cn(
              "fixed bottom-0 left-0 right-0 z-[60] rounded-t-3xl p-6 pb-28 border border-white/60 overflow-y-auto max-h-[88vh] md:max-w-lg md:mx-auto md:left-0 md:right-0 md:bottom-8 md:rounded-3xl md:pb-8 md:max-h-none",
              cardBg,
            )}
          >
            <div className="w-10 h-1 rounded-full bg-foreground/20 mx-auto mb-5 md:hidden" />

            <div className="flex items-center gap-2 absolute top-5 right-5">
              {playbook.isCustom && playbook.canDelete && onDelete && (
                <button
                  onClick={onDelete}
                  className="w-8 h-8 rounded-full bg-destructive/10 hover:bg-destructive/20 flex items-center justify-center transition-colors"
                  title="Delete trigger"
                >
                  <Trash2 className="w-3.5 h-3.5 text-destructive" />
                </button>
              )}
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-white/50 flex items-center justify-center hover:bg-white transition-colors"
              >
                <X className="w-4 h-4 text-foreground/60" />
              </button>
            </div>

            <div className="mb-5 pr-10">
              <div className="flex items-start gap-3 mb-3">
                <PlaybookThumbnail playbook={playbook} size="lg" />
                <div className="min-w-0 flex-1 pt-0.5">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-foreground/50 block">
                    {playbook.category}
                    {playbook.author && ` · ${playbook.author}`}
                  </span>
                </div>
              </div>

              <PlaybookTitle title={playbook.title} variant="preview" className="mb-3" />

              <p className="text-sm text-foreground/70 leading-relaxed">
                {truncatePlaybookDescription(playbook.description)}
              </p>

              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2.5">
                {playbook.videoUrl && (
                  <a
                    href={playbook.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-foreground/50 hover:text-foreground/70 transition-colors"
                  >
                    <PlaybookSourceIcon sourceType={playbook.sourceType} videoUrl={playbook.videoUrl} />
                    {playbookSourceLabel(playbook)}
                  </a>
                )}
                <span className="inline-flex items-center gap-1 text-xs text-foreground/50">
                  <Clock className="w-3 h-3" />
                  {formatPlaybookDurationLabel(playbook)}
                </span>
              </div>
            </div>

            <div className="space-y-2 mb-5">
              <p className="text-xs font-bold uppercase tracking-widest text-foreground/50 mb-3">What you'll do</p>
              {[
                playbookRecallStep(playbook),
                "Unfold through a personalised sequence of questions",
                "Receive your Unfolding — a full clarity snapshot",
              ].map((step, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-white/60 flex items-center justify-center text-[10px] font-bold text-foreground/60 shrink-0">
                    {i + 1}
                  </div>
                  <p className="text-sm text-foreground/70">{step}</p>
                </div>
              ))}
            </div>

            {startError && (
              <p className="text-sm text-center px-2 py-2.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 mb-1">
                {startError}
              </p>
            )}

            <button
              onClick={onStart}
              disabled={isStarting}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-primary text-white font-medium hover:bg-primary/90 transition-all shadow-sm disabled:opacity-50"
            >
              <Play className="w-4 h-4" />
              {isStarting ? "Starting…" : "Begin reflection session"}
            </button>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
